/**
 * Reviews list — filter by cycle, show employee name + reviewer + type + status.
 * Admin/Manager can create a new review via a modal.
 *
 * Employee names are resolved client-side from the employees list (server
 * already scopes both lists to what the viewer may see). Cycle filtering is
 * also client-side because the review list endpoint filters by employeeId only.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Star, Plus, Download } from 'lucide-react';
import { useReviews, useReviewCycles, useCreateReview, useCreateCycle } from '../api.js';
import { useEmployees } from '../../employees/api.js';
import { EmployeeSelect } from '../../employees/components/EmployeeSelect.js';
import { useAuthStore } from '../../../store/auth.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Select, Field, Textarea, Input } from '../../../shell/Field.js';
import { Modal } from '../../../shell/Modal.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { downloadCsv } from '../../../lib/csv.js';
import { REVIEW_CYCLE_TYPE, REVIEW_CYCLE_STATUS } from '@shared/enums/index.js';
import type { Review, AnyReviewRatings } from '@shared/types';
import { isWeeklyScorecard } from '@shared/util/weekly-scorecard.js';
import {
  WeeklyScorecardFields,
  emptyWeeklyDraft,
  draftToWeeklyScorecard,
  type WeeklyScorecardDraft,
} from '../components/WeeklyScorecardFields.js';

function avgRating(r: AnyReviewRatings): number {
  if (isWeeklyScorecard(r)) return r.overallPerformanceScore;
  const vals = Object.values(r);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function scoreLabel(r: AnyReviewRatings): string {
  if (isWeeklyScorecard(r)) return `${r.overallPerformanceScore.toFixed(1)}`;
  return avgRating(r).toFixed(1);
}

export function ReviewListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [cycleId, setCycleId] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [cycleOpen, setCycleOpen] = useState(false);

  const { data, isLoading } = useReviews();
  const { data: cyclesData } = useReviewCycles();
  const { data: employeesData } = useEmployees();

  const canCreate = user?.role === 'Admin' || user?.role === 'Manager';

  const employeeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employeesData?.items ?? []) map.set(e.id, e.name);
    return map;
  }, [employeesData]);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    if (!cycleId) return items;
    return items.filter((r) => r.cycleId === cycleId);
  }, [data, cycleId]);

  const columns: Column<Review>[] = [
    {
      key: 'employee',
      header: 'Employee',
      render: (r) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {employeeName.get(r.employeeId) ?? r.employeeId.slice(-6)}
        </span>
      ),
    },
    {
      key: 'reviewer',
      header: 'Reviewer',
      render: (r) => (
        <span className="text-slate-600 dark:text-slate-400">
          {employeeName.get(r.reviewerId) ?? r.reviewerId.slice(-6)}
        </span>
      ),
    },
    { key: 'type', header: 'Type', render: (r) => r.type },
    {
      key: 'avg',
      header: 'Score',
      render: (r) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{scoreLabel(r.ratings)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge tone={r.status === 'Shared' ? 'green' : 'yellow'}>{r.status}</Badge>,
    },
  ];

  const cycleName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cyclesData?.items ?? []) map.set(c.id, c.name);
    return map;
  }, [cyclesData]);

  const handleExport = () => {
    downloadCsv(
      `reviews-${new Date().toISOString().slice(0, 10)}.csv`,
      filtered.map((r) => ({
        Employee: employeeName.get(r.employeeId) ?? r.employeeId,
        Reviewer: employeeName.get(r.reviewerId) ?? r.reviewerId,
        Cycle: cycleName.get(r.cycleId) ?? r.cycleId,
        Type: r.type,
        Status: r.status,
        AvgRating: scoreLabel(r.ratings),
      })),
    );
    toast('success', 'CSV downloaded.');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Performance reviews</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{filtered.length} reviews</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExport} disabled={filtered.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          {canCreate && (
            <>
              <Button variant="secondary" onClick={() => setCycleOpen(true)}>
                <Plus className="h-4 w-4" /> New Cycle
              </Button>
              <Button onClick={() => setCreateOpen(true)} disabled={(cyclesData?.items.length ?? 0) === 0}>
                <Plus className="h-4 w-4" /> New Review
              </Button>
            </>
          )}
        </div>
      </div>

      {(cyclesData?.items.length ?? 0) > 0 ? (
        <div className="max-w-xs">
          <Select
            label="Cycle"
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
          >
            <option value="">All cycles</option>
            {(cyclesData?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type}) — {c.status}
              </option>
            ))}
          </Select>
        </div>
      ) : canCreate ? (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          No review cycles yet — create a cycle before adding reviews.
        </p>
      ) : null}

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : filtered.length > 0 ? (
        <Table columns={columns} data={filtered} onRowClick={(r) => navigate(`/reviews/${r.id}`)} />
      ) : (
        <EmptyState
          icon={<Star className="h-8 w-8" />}
          title="No reviews found"
          description={
            canCreate
              ? 'Create a review cycle, then add a review to get started.'
              : 'Reviews shared with you will appear here.'
          }
        />
      )}

      {canCreate && (
        <>
          <CreateCycleModal open={cycleOpen} onClose={() => setCycleOpen(false)} />
          <CreateReviewModal open={createOpen} onClose={() => setCreateOpen(false)} />
        </>
      )}
    </div>
  );
}


function CreateCycleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateCycle();
  const [name, setName] = useState('');
  const [type, setType] = useState<(typeof REVIEW_CYCLE_TYPE)[number]>('Quarterly');
  const [status, setStatus] = useState<(typeof REVIEW_CYCLE_STATUS)[number]>('Open');
  const [opensAt, setOpensAt] = useState('');
  const [closesAt, setClosesAt] = useState('');

  const reset = () => {
    setName('');
    setType('Quarterly');
    setStatus('Open');
    setOpensAt('');
    setClosesAt('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await create.mutateAsync({
        name,
        type,
        status,
        opensAt: new Date(`${opensAt}T00:00:00.000Z`).toISOString(),
        closesAt: new Date(`${closesAt}T23:59:59.000Z`).toISOString(),
      });
      toast('success', 'Review cycle created.');
      handleClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to create cycle.';
      toast('error', msg);
    }
  };

  const canSubmit = Boolean(name && opensAt && closesAt);

  return (
    <Modal open={open} onClose={handleClose} title="New Review Cycle">
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Q3 2026 Performance"
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as (typeof REVIEW_CYCLE_TYPE)[number])}
          >
            {REVIEW_CYCLE_TYPE.map((t) => (
              <option key={t} value={t}>{t === 'Weekly' ? 'Weekly 1:1' : t}</option>
            ))}
          </Select>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as (typeof REVIEW_CYCLE_STATUS)[number])}
          >
            {REVIEW_CYCLE_STATUS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Opens">
            <Input type="date" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} />
          </Field>
          <Field label="Closes">
            <Input type="date" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={create.isPending} disabled={!canSubmit}>
            Create Cycle
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateReviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateReview();
  const { data: cyclesData } = useReviewCycles();
  const { user } = useAuthStore();

  const [cycleId, setCycleId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState<string>('Annual');
  const [performance, setPerformance] = useState(3);
  const [culture, setCulture] = useState(3);
  const [communication, setCommunication] = useState(3);
  const [dependability, setDependability] = useState(3);
  const [feedback, setFeedback] = useState('');
  const [goals, setGoals] = useState('');
  const [weekly, setWeekly] = useState<WeeklyScorecardDraft>(emptyWeeklyDraft());

  const isWeekly = type === 'Weekly';

  const reset = () => {
    setCycleId('');
    setEmployeeId('');
    setType('Annual');
    setPerformance(3);
    setCulture(3);
    setCommunication(3);
    setDependability(3);
    setFeedback('');
    setGoals('');
    setWeekly(emptyWeeklyDraft());
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const ratings = isWeekly
        ? draftToWeeklyScorecard(weekly)
        : { performance, culture, communication, dependability };
      await create.mutateAsync({
        cycleId,
        employeeId,
        type: type as Review['type'],
        ratings,
        qualitativeFeedback: isWeekly ? weekly.wins : feedback,
        goalsForNextPeriod: isWeekly ? weekly.actions : goals,
        status: 'Draft',
      });
      toast('success', isWeekly ? 'Weekly 1:1 created as Draft.' : 'Review created as Draft.');
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to create review.';
      toast('error', msg);
    }
  };

  const canSubmit = Boolean(
    cycleId &&
      employeeId &&
      (!isWeekly || (weekly.periodStart && weekly.periodEnd)),
  );

  return (
    <Modal open={open} onClose={handleClose} title={isWeekly ? 'New Weekly 1:1' : 'New Review'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select label="Cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
            <option value="">Select a cycle…</option>
            {cyclesData?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
            ))}
          </Select>
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            {REVIEW_CYCLE_TYPE.map((t) => (
              <option key={t} value={t}>{t === 'Weekly' ? 'Weekly 1:1' : t}</option>
            ))}
          </Select>
        </div>

        <EmployeeSelect
          label="Account manager / employee"
          value={employeeId}
          onChange={setEmployeeId}
          excludeIds={user?.employeeId ? [user.employeeId] : []}
        />

        {isWeekly ? (
          <WeeklyScorecardFields value={weekly} onChange={setWeekly} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <RatingSelect label="Performance" value={performance} onChange={setPerformance} />
              <RatingSelect label="Culture" value={culture} onChange={setCulture} />
              <RatingSelect label="Communication" value={communication} onChange={setCommunication} />
              <RatingSelect label="Dependability" value={dependability} onChange={setDependability} />
            </div>
            <Textarea
              label="Qualitative feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
            />
            <Textarea
              label="Goals for next period"
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              rows={2}
            />
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={create.isPending}
            disabled={!canSubmit}
          >
            Create Draft
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RatingSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label}>
      <Select value={String(value)} onChange={(e) => onChange(Number(e.target.value))}>
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={n}>{n} / 5</option>
        ))}
      </Select>
    </Field>
  );
}
